import { defineStore } from 'pinia'
import { readonly, ref } from 'vue'
import type { User } from '@/features/api/types'
import { useApi } from '@/features/api/useAPI'
import { usePreferencesStore } from '@/features/preferences/usePreferencesStore'
import { type TelemetryTransaction } from '@/features/telemetry/types'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { useFeatureFlagStore } from '@/features/user/useFeatureFlagStore'
import { get } from '@/features/user/userRemoteRepository'

export const useUserStore = defineStore('user', () => {
	const { setUserData } = useTelemetry()
	const userToken = ref<string | undefined>(undefined)
	const isUserInitialized = ref<boolean>(false)
	const { updateHeaderAuthorization } = useApi()

	const user = ref<User>()

	async function fetch() {
		user.value = await get()
	}

	const setUserToken = (token: string) => {
		userToken.value = token
		updateHeaderAuthorization(token)
	}

	async function initUser(transaction?: TelemetryTransaction) {
		const storeInitSpanWrapper = (storeName: string, fn: () => Promise<void>) => {
			const span = transaction?.startSpan(storeName, 'store initialization')

			return fn().finally(() => span?.end())
		}

		const span = transaction?.startSpan('user store', 'store initialization')

		await fetch()
		span?.end()

		setUserData(user.value)

		await Promise.allSettled([
			storeInitSpanWrapper('feature flag store', useFeatureFlagStore().refresh),
			storeInitSpanWrapper('preferences store', usePreferencesStore().fetch)
		])

		isUserInitialized.value = true
	}

	return {
		fetch,
		initUser,
		user,
		userToken: readonly(userToken),
		setUserToken,
		isUserInitialized
	}
})
